import tokens from '@kluein/design-tokens';
import { Box, Button } from '@kluein/klue-ui';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { AnimatePresence } from 'framer-motion';
import { type ReactElement, useCallback } from 'react';
import styled from 'styled-components';

import useScrollObserver from 'hooks/use-scroll-observer/useScrollObserver';
import TEST_IDS from 'test-ids';

/*
  Based on CSS variables in klue-ui Buttons styles
  How much of a buffer do we need around the container so that shadows aren't obscured?

  ```
  --box-shadow-offsets: 0px 2px;
  --box-shadow-blur: 8px;
  ```

  Small = 10px
  Medium = 13px
  Large = 16px
*/
const BOX_SHADOW_SPACE = 10;

const StyledFade = styled(Box)`
  height: calc(100% + ${BOX_SHADOW_SPACE * 2}px);
  top: -${BOX_SHADOW_SPACE}px;
`;

const Fade = ({ ...props }: any) => (
  <AnimatePresence>
    <StyledFade
      data-test-id={props['data-test-id']}
      height="100%"
      width="small"
      style={{
        position: 'absolute',
        left: props.left,
        right: props.right,
        opacity: props.isVisible ? 1 : 0,
        background: props.background,
        transition: 'opacity 0.2s ease-out',
        pointerEvents: 'none',
      }}
    />
  </AnimatePresence>
);

const ScrollContainer = styled('div')`
  display: block;
  margin: -${BOX_SHADOW_SPACE}px;
  padding: ${BOX_SHADOW_SPACE}px;
  margin-right: 0;
  max-width: unset;
  width: calc(100% + ${BOX_SHADOW_SPACE}px);

  /* chrome */
  ::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
`;

const FADE_GRADIENT = 'rgba(255, 255, 255, 1) 10%, rgba(255, 255, 255, 0) 100%';
const SCROLL_GAP = 200;

export const HorizontalScroller = ({
  gradient = FADE_GRADIENT,
  enableControl = false,
  scrollGap = SCROLL_GAP,
  children,
}: {
  gradient?: string;
  enableControl?: boolean;
  scrollGap?: number;
  children?: ReactElement;
}) => {
  const {
    scrollState: { isOverflowedX, isLeft, isRight },
    refs: { observeScroll, observedNode },
  } = useScrollObserver();

  const scrollTo = useCallback(
    (direction: 'left' | 'right') => {
      const scrollContainer = observedNode.current;
      if (scrollContainer) {
        const scrollAmount = direction === 'left' ? -scrollGap : scrollGap;
        scrollContainer.scrollBy({
          left: scrollAmount,
          behavior: 'smooth',
        });
      }
    },
    [observedNode, scrollGap],
  );

  return (
    <Box direction="row" gap="small">
      <Box direction="row" style={{ position: 'relative' }}>
        <ScrollContainer ref={observeScroll} style={{ overflowX: 'scroll' }}>
          <Fade
            data-test-id={TEST_IDS.layout.horizontalScroller.fade.left}
            left={BOX_SHADOW_SPACE * -1}
            isVisible={isOverflowedX && !isLeft}
            background={`linear-gradient(to right, ${gradient})`}
          />
          {children}
          <Fade
            data-test-id={TEST_IDS.layout.horizontalScroller.fade.right}
            right={0}
            isVisible={isOverflowedX && !isRight}
            background={`linear-gradient(to left, ${gradient})`}
          />
        </ScrollContainer>
      </Box>
      {enableControl && (
        <Box direction="row" align="center" flex={{ grow: 1 }} justify="end">
          <Box align="center" justify="center">
            <Button
              variant="flat-form"
              onClick={() => scrollTo('left')}
              disabled={isOverflowedX && isLeft}
              icon={<CaretLeft size={tokens.iconSize.small} />}
            />
          </Box>
          <Box align="center" justify="center">
            <Button
              variant="flat-form"
              onClick={() => scrollTo('right')}
              disabled={isOverflowedX && isRight}
              icon={<CaretRight size={tokens.iconSize.small} />}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default HorizontalScroller;
